import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faExclamationTriangle,
  faCheckCircle,
  faArrowRight,
  faTimes,
  faRedo
} from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';

import { workflowAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import TemplateRenderer from '../TemplateRenderers/TemplateRenderer';

/**
 * UniversalStepManager - Handles any UI→AI→UI workflow sequence
 *
 * This component:
 * 1. Checks backend for current state (UI component or chat)
 * 2. Renders UI components from workflow YAML definitions
 * 3. Handles form submissions and backend function execution
 * 4. Manages transitions between UI and chat interfaces
 * 5. Maintains backward compatibility with AI-first workflows
 */
const UniversalStepManager = ({
  workflowId,
  initialRunId = null,
  onChatRedirect = null,
  onWorkflowComplete = null,
  onError = null,
  onCancel = null,  // Callback for cancel/close - parent handles navigation
  source = 'workflow',  // 'agent' or 'workflow' - determines cancel redirect
  agentId = null  // Agent ID for back navigation when source is 'agent'
}) => {
  console.log('🟢 FIXED UNIVERSALSTEPMANAGER LOADED - NEW VERSION!');
  const navigate = useNavigate();
  const { token } = useAuth();

  // State management
  const [currentState, setCurrentState] = useState(null);
  const [runId, setRunId] = useState(initialRunId);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // Ref to track polling interval for bank workflow
  const bankPollingIntervalRef = useRef(null);
  const bankPollingTimeoutRef = useRef(null);

  // Check current state from backend (source of truth)
  // isPolling flag prevents setting loading state during background polls
  const checkCurrentState = useCallback(async (isPolling = false) => {
    try {
      // Only show loading spinner on initial load, not during polling
      if (!isPolling) {
        setLoading(true);
      }
      setError(null);

      console.log(`🔍 Checking workflow state: workflowId=${workflowId}, runId=${runId}, isPolling=${isPolling}`);

      const state = await workflowAPI.getCurrentState(workflowId, runId);

      console.log('📊 Backend state response:', state);
      setCurrentState(state);

      // Handle different states
      switch (state.state) {
        case 'not_started_chat':
        case 'running_chat':
          // Budget Agent (28) - no chat page, treat as UI workflow
          if (parseInt(workflowId) === 28) {
            console.log('📊 Budget Agent - no chat redirect, treating as UI workflow');
            if (state.run_id) {
              setRunId(state.run_id);
            }
            setCurrentState(state);
            setLoading(false);
            break;
          }

          // ✅ BACKWARD COMPATIBILITY: Redirect to chat for AI-first workflows
          console.log('🔄 Redirecting to chat interface');
          if (onChatRedirect) {
            onChatRedirect(state.run_id || null);
          } else if (parseInt(workflowId) === 15) {
            // Sale Order workflow (NetSuite) - redirect to dedicated page
            const saleRunId = runId || state.run_id;
            navigate(saleRunId ? `/sale-order-agent?runId=${saleRunId}` : '/sale-order-agent');
          } else if (parseInt(workflowId) === 21) {
            // SF Sale Order workflow (Salesforce) - redirect to dedicated page
            const sfSaleRunId = runId || state.run_id;
            navigate(sfSaleRunId ? `/sale-order-sf?runId=${sfSaleRunId}` : '/sale-order-sf');
          } else {
            const chatUrl = runId || state.run_id
              ? `/workflows/${workflowId}/chat?run_id=${runId || state.run_id}`
              : `/workflows/${workflowId}/chat`;
            navigate(chatUrl);
          }
          break;

        case 'not_started_ui':
        case 'running_ui':
          // RFQ Workflow (id=25) with custom component - redirect to dedicated page
          // Check both paths: config.ui_component (flat) and config.config.ui_component (nested from YAML)
          const hasCustomComponent = state.current_step?.config?.ui_component ||
                                     state.current_step?.config?.config?.ui_component;
          if (parseInt(workflowId) === 25 && hasCustomComponent) {
            console.log('🔄 RFQ workflow custom component - redirecting to /rfq-workflow/run/', state.run_id);
            navigate(`/rfq-workflow/run/${state.run_id}`);
            return;
          }

          // UI step - will be rendered by this component
          console.log('🖥️ Rendering UI component:', state.current_step?.name);
          if (state.run_id) {
            setRunId(state.run_id);
          }

          // Budget Agent (28) - stays in this component, must set loading=false to render UI
          // Other agents redirect to dedicated pages, so this only affects Budget Agent
          if (parseInt(workflowId) === 28) {
            setLoading(false);
          }
          break;

        case 'completed':
          console.log('✅ Workflow completed');
          if (onWorkflowComplete) {
            onWorkflowComplete(state);
          }
          break;

        default:
          console.warn('⚠️ Unknown workflow state:', state.state);
          break;
      }

    } catch (err) {
      console.error('❌ Failed to check workflow state:', err);

      // ✅ BACKWARD COMPATIBILITY: Fallback to chat on any error
      if (retryCount < 2) {
        console.log('🔄 Retrying state check...');
        setRetryCount(prev => prev + 1);
        setTimeout(checkCurrentState, 1000);
        return;
      }

      // Budget Agent (28) - no chat fallback, just show error
      if (parseInt(workflowId) === 28) {
        console.log('📊 Budget Agent - state check failed, showing error (no chat redirect)');
        setError('Failed to determine workflow state. Please try again.');
        return;
      }

      console.log('🔄 State check failed, falling back to chat interface');
      setError('Failed to determine workflow state. Redirecting to chat...');

      setTimeout(() => {
        if (onChatRedirect) {
          onChatRedirect(runId);
        } else {
          navigate(`/workflows/${workflowId}/chat`);
        }
      }, 2000);

    } finally {
      setLoading(false);
    }
  }, [workflowId, runId, onChatRedirect, onWorkflowComplete, navigate, retryCount]);

  // Initialize component
  useEffect(() => {
    checkCurrentState();
  }, [checkCurrentState]);

  // Watch for RFQ workflow success modal - show SweetAlert and navigate
  useEffect(() => {
    if (parseInt(workflowId) === 25 &&
        currentState?.current_step?.name === 'rfq_created_success_ui') {
      console.log('✅ 📋 RFQ workflow reached success step - showing SweetAlert');

      // Get RFQ details from context
      const context = currentState?.context || {};
      const rfqNumber = context.rfq_number || 'N/A';
      const netsuiteRfqId = context.netsuite_rfq_id || 'N/A';
      const vendorCount = (context.saved_vendors || []).length;
      const itemCount = (context.saved_items || []).length;

      Swal.fire({
        icon: 'success',
        title: 'RFQ Created Successfully!',
        html: `
          <div style="text-align: left; margin-top: 10px;">
            <p><strong>RFQ Number:</strong> ${rfqNumber}</p>
            <p><strong>NetSuite ID:</strong> ${netsuiteRfqId}</p>
            <p><strong>Items:</strong> ${itemCount}</p>
            <p><strong>Vendors:</strong> ${vendorCount}</p>
          </div>
        `,
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Go to RFQ Workflow Dashboard'
      }).then(() => {
        navigate('/rfq-workflow');
      });
    }
  }, [currentState, workflowId, navigate]);

  // Watch for state changes and stop bank polling when results are ready
  useEffect(() => {
    if (parseInt(workflowId) === 16 &&
        bankPollingIntervalRef.current &&
        currentState?.current_step?.name === 'display_results_ui_step') {
      console.log('✅ 🏦 Bank results ready - stopping polling');
      clearInterval(bankPollingIntervalRef.current);
      bankPollingIntervalRef.current = null;
      if (bankPollingTimeoutRef.current) {
        clearTimeout(bankPollingTimeoutRef.current);
        bankPollingTimeoutRef.current = null;
      }
      setSubmitting(false);
    }
  }, [currentState, workflowId]);

  // Budget Agent (28) - Track step changes and stop polling when next UI step is ready
  const budgetPreviousStepRef = React.useRef(null);
  useEffect(() => {
    if (parseInt(workflowId) !== 28) return;

    const currentStepName = currentState?.current_step?.name;
    const stepType = currentState?.current_step?.type || currentState?.current_step?.config?.type;

    // If step has changed and we have a component (new UI step ready)
    if (bankPollingIntervalRef.current &&
        currentStepName &&
        currentStepName !== budgetPreviousStepRef.current) {
      // Check if new step is a UI step or has a component
      const hasComponent = currentState?.current_step?.config?.component ||
                          currentState?.current_step?.component_definition;

      if (stepType === 'ui_step' || hasComponent) {
        console.log('📊 Budget Agent - new UI step ready:', currentStepName);
        clearInterval(bankPollingIntervalRef.current);
        bankPollingIntervalRef.current = null;
        if (bankPollingTimeoutRef.current) {
          clearTimeout(bankPollingTimeoutRef.current);
          bankPollingTimeoutRef.current = null;
        }
        setSubmitting(false);
        setLoading(false);
      }

      // Update previous step reference
      budgetPreviousStepRef.current = currentStepName;
    }
  }, [currentState, workflowId]);

  // Handle form submission for UI steps
  const handleFormSubmit = async (formData) => {
    try {
      setSubmitting(true);
      setError(null);

      console.log('📝 Submitting form data:', formData);

      let result;
      const currentRunId = runId || currentState?.run_id;

      // Check if formData contains files
      const hasFiles = Object.values(formData).some(value => value instanceof File);

      if (!currentRunId) {
        let newRunId;

        if (hasFiles) {
          // For file uploads with no runId, start workflow first then submit files
          console.log('🚀 Starting new workflow with files - using two-step process');

          // Step 1: Start workflow with minimal data
          const startResult = await workflowAPI.startWorkflowV2(workflowId, '{}', token);
          if (!startResult.run_id) {
            throw new Error('Failed to start workflow - no run_id returned');
          }

          newRunId = startResult.run_id;
          setRunId(newRunId);
          console.log(`✅ Workflow started with run_id: ${newRunId}`);

          // Step 2: Submit form with files to the new run_id
          console.log('📝 Submitting form with files to new workflow');
          result = await workflowAPI.submitUIForm(newRunId, formData, token, workflowId);
        } else {
          // No files, use standard workflow start
          console.log('🚀 Starting new workflow for initial UI step (no files)');
          result = await workflowAPI.startWorkflowV2(workflowId, JSON.stringify(formData), token);
          newRunId = result.run_id;
          setRunId(newRunId);
        }

        // Check if result contains transition info for immediate navigation
        if (result.data?.transition_to === 'chat') {
          // Budget Agent (28) - no chat redirect, poll for next step
          if (parseInt(workflowId) === 28) {
            console.log('📊 Budget Agent - workflow started, polling for next step');
            toast.success('Workflow started successfully!');
            await checkCurrentState();
            setSubmitting(false);
            return;
          }

          console.log('🔄 Initial workflow started, redirecting to chat');
          toast.success('Workflow started successfully! Redirecting to chat...');

          setTimeout(() => {
            if (onChatRedirect) {
              onChatRedirect(newRunId);
            } else if (parseInt(workflowId) === 15) {
              // Sale Order workflow (NetSuite) - redirect to dedicated page
              navigate(`/sale-order-agent?runId=${newRunId}`);
            } else if (parseInt(workflowId) === 21) {
              // SF Sale Order workflow (Salesforce) - redirect to dedicated page
              navigate(`/sale-order-sf?runId=${newRunId}`);
            } else {
              // All other workflows - use default chat page
              navigate(`/workflows/${workflowId}/chat?run_id=${newRunId}`);
            }
          }, 1000);
          return;
        }

        if (result.data?.transition_to === 'completed') {
          // Budget Agent (28) - no chat redirect, show completion
          if (parseInt(workflowId) === 28) {
            console.log('📊 Budget Agent - workflow completed');
            toast.success('Budget workflow completed successfully!');
            await checkCurrentState();
            setSubmitting(false);
            return;
          }

          console.log('🔄 Initial workflow completed, redirecting to chat to show results');
          toast.success('Processing completed! Redirecting to chat...');

          setTimeout(() => {
            if (onChatRedirect) {
              onChatRedirect(newRunId);
            } else if (parseInt(workflowId) === 15) {
              // Sale Order workflow (NetSuite) - redirect to dedicated page
              navigate(`/sale-order-agent?runId=${newRunId}`);
            } else if (parseInt(workflowId) === 21) {
              // SF Sale Order workflow (Salesforce) - redirect to dedicated page
              navigate(`/sale-order-sf?runId=${newRunId}`);
            } else {
              navigate(`/workflows/${workflowId}/chat?run_id=${newRunId}`);
            }
          }, 1000);
          return;
        }

        toast.success('Workflow started successfully!');
      } else {
        // Submit UI form for existing workflow
        console.log('📝 Submitting UI form for existing workflow');
        result = await workflowAPI.submitUIForm(currentRunId, formData, token, workflowId);

        if (result.data?.transition_to === 'chat') {
          // Budget Agent (28) - no chat page, poll for next UI step instead
          if (parseInt(workflowId) === 28) {
            console.log('📊 Budget Agent - polling for next step (no chat redirect)');
            toast.info('Processing... Please wait.');

            // Poll for state change like Bank Agent
            if (bankPollingIntervalRef.current) {
              clearInterval(bankPollingIntervalRef.current);
            }
            bankPollingIntervalRef.current = setInterval(async () => {
              try {
                await checkCurrentState(true);  // isPolling=true prevents loading re-render
              } catch (err) {
                console.error('❌ Polling error:', err);
                if (bankPollingIntervalRef.current) {
                  clearInterval(bankPollingIntervalRef.current);
                  bankPollingIntervalRef.current = null;
                }
                setSubmitting(false);
              }
            }, 2000);

            bankPollingTimeoutRef.current = setTimeout(() => {
              if (bankPollingIntervalRef.current) {
                clearInterval(bankPollingIntervalRef.current);
                bankPollingIntervalRef.current = null;
              }
              setSubmitting(false);
            }, 120000);
            return;
          }

          // UI step completed, redirect to chat
          console.log('🔄 UI step completed, redirecting to chat');
          toast.success('Form submitted successfully! Redirecting to chat...');

          // Redirect to chat after a short delay
          setTimeout(() => {
            if (onChatRedirect) {
              onChatRedirect(currentRunId);
            } else if (parseInt(workflowId) === 15) {
              // Sale Order workflow (NetSuite) - redirect to dedicated page
              navigate(`/sale-order-agent?runId=${currentRunId}`);
            } else if (parseInt(workflowId) === 21) {
              // SF Sale Order workflow (Salesforce) - redirect to dedicated page
              navigate(`/sale-order-sf?runId=${currentRunId}`);
            } else {
              navigate(`/workflows/${workflowId}/chat?run_id=${currentRunId}`);
            }
          }, 1000);
          return;
        }

        if (result.data?.transition_to === 'completed') {
          // Budget Agent (28) - no chat page, show completion on current page
          if (parseInt(workflowId) === 28) {
            console.log('📊 Budget Agent - workflow completed');
            toast.success('Budget workflow completed successfully!');
            await checkCurrentState();
            setSubmitting(false);
            return;
          }

          // Workflow completed, redirect to chat to show final result
          console.log('🔄 Workflow completed, redirecting to chat to show results');
          toast.success('Processing completed! Redirecting to chat...');

          // Redirect to chat after a short delay
          setTimeout(() => {
            if (onChatRedirect) {
              onChatRedirect(currentRunId);
            } else if (parseInt(workflowId) === 15) {
              // Sale Order workflow (NetSuite) - redirect to dedicated page
              navigate(`/sale-order-agent?runId=${currentRunId}`);
            } else if (parseInt(workflowId) === 21) {
              // SF Sale Order workflow (Salesforce) - redirect to dedicated page
              navigate(`/sale-order-sf?runId=${currentRunId}`);
            } else {
              navigate(`/workflows/${workflowId}/chat?run_id=${currentRunId}`);
            }
          }, 1000);
          return;
        }

        toast.success('Step completed successfully!');

        // Budget Agent (28) - poll for next step after any submission (no chat redirect)
        if (parseInt(workflowId) === 28) {
          console.log('📊 Budget Agent - step completed, polling for next step');

          // Clear any existing polling
          if (bankPollingIntervalRef.current) {
            clearInterval(bankPollingIntervalRef.current);
          }

          // Poll for state change
          bankPollingIntervalRef.current = setInterval(async () => {
            try {
              await checkCurrentState(true);  // isPolling=true prevents loading re-render
            } catch (err) {
              console.error('❌ Budget polling error:', err);
              if (bankPollingIntervalRef.current) {
                clearInterval(bankPollingIntervalRef.current);
                bankPollingIntervalRef.current = null;
              }
              setSubmitting(false);
            }
          }, 2000);

          // Timeout after 2 minutes
          bankPollingTimeoutRef.current = setTimeout(() => {
            if (bankPollingIntervalRef.current) {
              clearInterval(bankPollingIntervalRef.current);
              bankPollingIntervalRef.current = null;
            }
            setSubmitting(false);
          }, 120000);

          return;
        }
      }

      console.log('✅ Form submission result:', result);
      console.log('🔍 Bank workflow check:', { workflowId, parsedWorkflowId: parseInt(workflowId), hasFiles, isBankWorkflow: parseInt(workflowId) === 16 });

      // For bank agent (workflowId === 16), keep submitting=true and poll for state change
      if (parseInt(workflowId) === 16 && hasFiles) {
        console.log('🏦 Bank workflow submitted - keeping spinner visible and polling for results...');

        // Clear any existing polling intervals
        if (bankPollingIntervalRef.current) {
          clearInterval(bankPollingIntervalRef.current);
        }
        if (bankPollingTimeoutRef.current) {
          clearTimeout(bankPollingTimeoutRef.current);
        }

        // Start polling for state change - don't set submitting=false yet
        bankPollingIntervalRef.current = setInterval(async () => {
          try {
            await checkCurrentState();
          } catch (err) {
            console.error('❌ Polling error:', err);
            if (bankPollingIntervalRef.current) {
              clearInterval(bankPollingIntervalRef.current);
              bankPollingIntervalRef.current = null;
            }
            setSubmitting(false);
          }
        }, 2000); // Poll every 2 seconds

        // Safety timeout - stop polling after 2 minutes
        bankPollingTimeoutRef.current = setTimeout(() => {
          if (bankPollingIntervalRef.current) {
            clearInterval(bankPollingIntervalRef.current);
            bankPollingIntervalRef.current = null;
          }
          setSubmitting(false);
        }, 120000);
      } else {
        // For other workflows, refresh state immediately
        await checkCurrentState();
        setSubmitting(false);
      }

    } catch (err) {
      console.error('❌ Form submission failed:', err);
      setError(err.response?.data?.detail || err.message || 'Form submission failed');

      if (onError) {
        onError(err);
      }

      toast.error('Form submission failed. Please try again.');
      setSubmitting(false);
    }
  };

  // Handle retry on error
  const handleRetry = () => {
    setRetryCount(0);
    checkCurrentState();
  };

  // Handle close/cancel - Save draft data
  const handleClose = async (cancelMessage = null) => {
    try {
      // For bank agent (workflowId === 16), use bank-specific save-draft endpoint
      if (parseInt(workflowId) === 16) {
        console.log('🏦 Saving bank workflow draft...');
        const response = await fetch(`/api/bank/v2/workflows/save-draft`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            run_id: runId
          })
        });

        if (!response.ok) {
          throw new Error(`Failed to save bank draft: ${response.status}`);
        }

        console.log('✅ Bank workflow draft saved successfully');
      } else {
        // For other workflows, use generic save-draft
        const draftData = {
          workflowId,
          runId,
          currentState: currentState?.state,
          stepName: currentState?.current_step?.name,
          stepConfig: currentState?.current_step,
          timestamp: new Date().toISOString()
        };

        await workflowAPI.saveDraftWorkflow(workflowId, runId, draftData, token);
        console.log('✅ Draft saved successfully');
      }
    } catch (err) {
      console.error('❌ Failed to save draft:', err);
      // Continue to close even if save fails
    }

    // If parent provided onCancel callback, use it (parent handles navigation)
    if (onCancel) {
      onCancel();
      return;
    }

    if (onChatRedirect) {
      onChatRedirect(runId, cancelMessage || "Workflow paused. You can resume anytime from chat.");
    } else if (agentId) {
      // If opened from agent, go back to agent page
      navigate(`/agents/${agentId}`);
    } else {
      // Default: go to workflow chat
      const chatUrl = runId
        ? `/workflows/${workflowId}/chat?run_id=${runId}&message=${encodeURIComponent(cancelMessage || "Workflow paused. You can resume anytime.")}`
        : `/workflows/${workflowId}/chat?message=${encodeURIComponent(cancelMessage || "Workflow paused. You can resume anytime.")}`;
      navigate(chatUrl);
    }
  };

  // Debug current render state
  console.log('🎯 UniversalStepManager render - currentState:', {
    loading,
    error: !!error,
    currentState: currentState?.state,
    hasCurrentStep: !!currentState?.current_step,
    fullState: currentState
  });

  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="text-4xl text-blue-600 animate-spin mb-4" />
          <p className="text-gray-600 dark:text-gray-400">
            Loading workflow...
          </p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error && !currentState) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-red-500 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Workflow Loading Error
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {error}
          </p>
          <div className="flex space-x-3 justify-center">
            <button
              onClick={handleRetry}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <FontAwesomeIcon icon={faRedo} />
              <span>Retry</span>
            </button>
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Continue to Chat
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Budget Agent (28) - dedicated UI rendering (no chat page)
  // This handles all states for Budget Agent since it's form-based only
  if (parseInt(workflowId) === 28 && currentState?.current_step) {
    const stepConfig = currentState.current_step;
    // Budget workflow: API returns current_step.config (YAML step) and current_step.config.component
    const componentDefinition = stepConfig?.config?.component
      ? {
          ...stepConfig.config.component,
          actions: stepConfig.config.component.actions || stepConfig.config.actions || stepConfig.actions
        }
      : stepConfig?.component_definition || null;

    console.log('📊 Budget Agent render:', {
      state: currentState.state,
      stepName: stepConfig?.name,
      hasComponent: !!componentDefinition,
      configKeys: Object.keys(stepConfig?.config || {}),
      hasConfigComponent: !!stepConfig?.config?.component,
      hasActions: !!componentDefinition?.actions,
      actionsKeys: componentDefinition?.actions ? Object.keys(componentDefinition.actions) : []
    });

    // Budget Agent (28) ONLY: AI step processing - show loading indicator
    // Polling is handled in handleFormSubmit, NOT here (to avoid infinite re-renders like Bank Agent pattern)
    if (!componentDefinition && parseInt(workflowId) === 28) {
      const stepType = stepConfig?.type || stepConfig?.config?.type || '';
      const isAIStep = ['ai_step', 'AI', 'ai'].includes(stepType);

      if (isAIStep) {
        console.log('📊 Budget Agent (28) - AI step processing (render only):', stepConfig?.name);

        // NO setTimeout here - polling is handled in handleFormSubmit (Bank Agent pattern)
        return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8"
          >
            <div className="text-center">
              <FontAwesomeIcon icon={faSpinner} className="text-4xl text-blue-600 animate-spin mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {stepConfig?.name === 'categorize_accounts' ? 'Categorizing Accounts...' :
                 stepConfig?.name === 'generate_suggestions' ? 'Generating Budget Suggestions...' :
                 'AI Processing...'}
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Please wait while AI analyzes your data.
              </p>
            </div>
          </motion.div>
        );
      }

      // Budget Agent non-AI step with no component
      return (
        <div className="text-center py-8">
          <p className="text-gray-600">No component defined for this step.</p>
        </div>
      );
    }

    // Other agents: fallback for no component
    if (!componentDefinition) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-600">No component defined for this step.</p>
        </div>
      );
    }

    // Budget Agent (28) ONLY - Handle success modal (budget_complete step)
    if (parseInt(workflowId) === 28 && componentDefinition.template_type === 'modal' && componentDefinition.type === 'success') {
      console.log('📊 Budget Agent (28) - rendering success modal:', stepConfig?.name);
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center"
        >
          <FontAwesomeIcon icon={faCheckCircle} className="text-6xl text-green-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {componentDefinition.title || 'Success!'}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {componentDefinition.message || 'Operation completed successfully.'}
          </p>
          <div className="flex justify-center gap-4">
            {componentDefinition.buttons?.map((btn, idx) => (
              <button
                key={idx}
                onClick={async () => {
                  // For budget workflow, mark as completed and go to history (same as Bank Agent)
                  console.log('📊 Budget workflow success modal - marking as completed');
                  try {
                    await fetch(`/api/budget/v2/workflows/complete-run/${runId}`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                      }
                    });
                    console.log('✅ Budget workflow marked as completed');
                  } catch (err) {
                    console.error('Failed to mark workflow as completed:', err);
                  }
                  navigate('/budget-agent/history');
                }}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  btn.variant === 'primary'
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200'
                }`}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </motion.div>
      );
    }

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-auto max-h-[85vh]"
      >
        <div className="p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {componentDefinition.metadata?.displayName || componentDefinition.metadata?.name || stepConfig.name}
            </h2>
            {componentDefinition.metadata?.description && (
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {componentDefinition.metadata.description}
              </p>
            )}
          </div>

          <TemplateRenderer
            definition={componentDefinition}
            token={token}
            onSubmit={handleFormSubmit}
            onClose={handleClose}
            loading={submitting}
            workflowContext={{
              workflowId,
              runId,
              stepName: stepConfig.name,
              stepType: stepConfig.type,
              source,
              agentId,
              ...(currentState?.workflow_context || {})
            }}
          />
        </div>
      </motion.div>
    );
  }

  // Render UI step (for other agents)
  if (currentState?.state === 'not_started_ui' || currentState?.state === 'running_ui') {
    const stepConfig = currentState.current_step;
    // IMPORTANT: Merge actions from step config into component definition
    // Actions are at stepConfig.config.actions, not inside component
    const componentDefinition = stepConfig?.config?.component
      ? {
          ...stepConfig.config.component,
          actions: stepConfig.config.actions  // Add actions for FormRenderer to show submit button
        }
      : null;

    console.log('🔧 UniversalStepManager rendering UI step:', {
      state: currentState.state,
      stepConfig: !!stepConfig,
      componentDefinition: !!componentDefinition,
      handleFormSubmit: typeof handleFormSubmit
    });

    if (!componentDefinition) {
      return (
        <div className="text-center py-8">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-yellow-500 mb-4" />
          <p className="text-gray-600 dark:text-gray-400">
            No UI component definition found for this step.
          </p>
          <button
            onClick={handleClose}
            className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Continue to Chat
          </button>
        </div>
      );
    }

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="workflow-ui-step"
      >
        {/* Step Header */}
        <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl border border-blue-200 dark:border-blue-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {componentDefinition.metadata?.displayName || componentDefinition.metadata?.name || stepConfig.name}
              </h2>
              {componentDefinition.metadata?.description && (
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  {componentDefinition.metadata.description}
                </p>
              )}
            </div>

            {/* Close button - only show if chat fallback available */}
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Switch to Chat"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Component Renderer */}
        <div className="workflow-ui-component">
          <TemplateRenderer
            definition={componentDefinition}
            token={token}
            onSubmit={handleFormSubmit}
            onClose={parseInt(workflowId) === 16 ? async () => {
              // For bank workflow TransactionsModal, mark workflow as completed and go to history
              console.log('🏦 Bank workflow TransactionsModal closed - marking as completed');
              try {
                // Mark the workflow run as completed so it doesn't show up again
                await fetch(`/api/bank/v2/workflows/complete-run/${runId}`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  }
                });
                console.log('✅ Bank workflow marked as completed');
              } catch (err) {
                console.error('Failed to mark workflow as completed:', err);
              }
              navigate('/bank-chat-history');
            } : handleClose}
            loading={submitting}
            workflowContext={{
              workflowId,
              runId,
              stepName: stepConfig.name,
              stepType: stepConfig.type,
              source,  // 'agent' or 'workflow'
              agentId,  // Pass agentId for navigation
              // Include workflow context data from API response (for custom components like Budget Agent)
              ...(currentState?.workflow_context || {})
            }}
          />
        </div>

        {/* Error Display */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg"
            >
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500" />
                <span className="text-red-700 dark:text-red-300">{error}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }

  // Render completion state
  if (currentState?.state === 'completed') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-12"
      >
        <FontAwesomeIcon icon={faCheckCircle} className="text-6xl text-green-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Workflow Completed!
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          All steps have been processed successfully.
        </p>
        <button
          onClick={() => navigate('/workflows-v2')}
          className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 mx-auto"
        >
          <span>Back to Workflows</span>
          <FontAwesomeIcon icon={faArrowRight} />
        </button>
      </motion.div>
    );
  }

  // Default fallback
  return (
    <div className="text-center py-8">
      <p className="text-gray-600 dark:text-gray-400">
        Loading workflow step...
      </p>
    </div>
  );
};

export default UniversalStepManager;